import { useCallback, useEffect, useMemo, useState } from "react";
import {
  backfillPerformance,
  getPerformanceStatus,
} from "../../../services/clearing/station-performance";

/** The API rejects a single call spanning more than this, so a long history is chunked. */
export const MAX_MONTHS_PER_CALL = 60;

export interface RunState {
  yearMonth: string;
  status: string;
  closureState: string;
  computedAt: string | null;
  sealedAt: string | null;
  resealCount: number;
  liveDayExcluded: boolean;
  sessionsRead: number;
  unitsWritten: number;
  rejectedBlankStation: number;
  rejectedBlankEvse: number;
  costFallbackApplied: number;
  durationMs: number;
  lastError: string | null;
}

export interface MonthDrift {
  yearMonth: string;
  closureState: string;
  storedSessionCount: number;
  liveSessionCount: number;
  missingFromAggregate: number;
  selfHealing: boolean;
  needsBackfill: boolean;
}

export interface ChunkResult {
  from: string;
  to: string;
  monthsQueued: number;
  sealedMonthsAffected: string[];
  error: string | null;
}

export interface BackfillProgress {
  running: boolean;
  chunksTotal: number;
  chunksDone: number;
  monthsProcessed: number;
  results: ChunkResult[];
}

const IDLE: BackfillProgress = {
  running: false,
  chunksTotal: 0,
  chunksDone: 0,
  monthsProcessed: 0,
  results: [],
};

const toYearMonth = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;

const addMonths = (yearMonth: string, months: number) => {
  const [y, m] = yearMonth.split("-").map(Number);
  const d = new Date(y, m - 1 + months, 1);
  return toYearMonth(d);
};

const monthsBetween = (from: string, to: string) => {
  const [fy, fm] = from.split("-").map(Number);
  const [ty, tm] = to.split("-").map(Number);
  return (ty - fy) * 12 + (tm - fm) + 1;
};

/**
 * Splits an arbitrary range into calls the API will accept.
 *
 * Exported for testing: an off-by-one here either silently skips a month or sends a range the API
 * rejects, and neither is visible from the progress bar alone.
 */
export const planChunks = (from: string, to: string): { from: string; to: string }[] => {
  if (monthsBetween(from, to) < 1) return [];
  const chunks: { from: string; to: string }[] = [];
  let start = from;
  while (monthsBetween(start, to) > 0) {
    const remaining = monthsBetween(start, to);
    const span = Math.min(remaining, MAX_MONTHS_PER_CALL);
    chunks.push({ from: start, to: addMonths(start, span - 1) });
    start = addMonths(start, span);
  }
  return chunks;
};

/**
 * The run state of every computed month, and a way to recompute a range.
 *
 * The backfill is the only path that can move a sealed month, so this is deliberately explicit about
 * what it touched: each chunk reports the sealed months it rewrote, and those are surfaced rather than
 * folded into a success count.
 */
export const useBackfill = () => {
  const [months, setMonths] = useState<RunState[] | null>(null);
  const [drift, setDrift] = useState<MonthDrift[]>([]);
  const [checkDrift, setCheckDrift] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState<BackfillProgress>(IDLE);

  const loadStatus = useCallback(async () => {
    setLoading(true);
    setError(null);
    const result = await getPerformanceStatus({ checkDrift });
    if (result?.success) {
      setMonths(result.data?.months ?? []);
      setDrift(result.data?.drift ?? []);
    } else {
      setError(result?.message ?? "Could not load the run state.");
    }
    setLoading(false);
  }, [checkDrift]);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  /**
   * The earliest month worth offering as a start.
   *
   * Nothing reports the date of the oldest session, so this is the earliest month already computed if
   * there is one. On a first ever run there is none, and the caller has to choose: the UI says so
   * rather than pretending a default is derived from the data.
   */
  const earliestComputed = useMemo(() => {
    if (!months || months.length === 0) return null;
    return months.map((m) => m.yearMonth).sort()[0];
  }, [months]);

  const run = useCallback(
    async (from: string, to: string) => {
      const chunks = planChunks(from, to);
      if (chunks.length === 0) return;

      setProgress({ ...IDLE, running: true, chunksTotal: chunks.length });

      const results: ChunkResult[] = [];
      let processed = 0;
      // Sequential on purpose. The backfill rewrites each month in full, and the endpoint is
      // synchronous, so firing chunks in parallel would put several whole-month recomputes on one
      // Lambda at once for no gain in wall-clock.
      for (const chunk of chunks) {
        // eslint-disable-next-line no-await-in-loop
        const result = await backfillPerformance(chunk);
        const entry: ChunkResult = {
          from: chunk.from,
          to: chunk.to,
          monthsQueued: result?.success ? (result.data?.monthsQueued ?? 0) : 0,
          sealedMonthsAffected: result?.success ? (result.data?.sealedMonthsAffected ?? []) : [],
          error: result?.success ? null : (result?.message ?? "Backfill failed."),
        };
        results.push(entry);
        processed += entry.monthsQueued;
        setProgress({
          running: true,
          chunksTotal: chunks.length,
          chunksDone: results.length,
          monthsProcessed: processed,
          results: [...results],
        });
      }

      setProgress({
        running: false,
        chunksTotal: chunks.length,
        chunksDone: results.length,
        monthsProcessed: processed,
        results,
      });
      await loadStatus();
    },
    [loadStatus],
  );

  return {
    months,
    drift,
    checkDrift,
    setCheckDrift,
    loading,
    error,
    progress,
    earliestComputed,
    run,
    reload: loadStatus,
  };
};
